/**
 * masterGanttMachineGrouping.ts
 * REQ-03 — Pivot + grouping helpers for the machine-grouped Master Gantt layout.
 *
 * This module owns the data-shaping half of the machine view: pivoting the
 * MasterGanttResponse into Machine → Project → Work Order buckets (with the
 * people who worked each WO on each machine), the row-id scheme, the
 * deterministic sort orders, the primary-machine selection for multi-machine
 * assignments, and the date-span helpers used by the envelope rows.
 *
 * The row-rendering half (the GanttPoint builders + the main
 * buildMachineGroupedPoints export) lives in masterGanttMachineRows.ts. Split
 * from that file to keep both under the 600-line CODING-STANDARDS cap.
 *
 * Row hierarchy (see masterGanttMachineRows.ts for the full doc-comment):
 *
 *   Machine     id = "mach-{machineKey}"
 *     Project   id = "machproj-{machineKey}-{projectKey}"
 *       WO      id = "machwo-{machineKey}-{workOrderKey}"
 *         Booking n   id = "booking-{bookingKey}"            (FIRST)
 *         People      id = "machwopeople-{machineKey}-{workOrderKey}"  (after bookings)
 *           Assign n  id = "assign-{assignmentKey}"
 */

import type {
  MasterGanttResponse,
  MasterGanttMachineBooking,
  MasterGanttEmployeeAssignment,
} from '../../types/schedule.js';

import { addDays } from './masterGanttRowBuilder.js';

// ---------------------------------------------------------------------------
// Intermediate grouping structures
// ---------------------------------------------------------------------------

/** One work-order bucket within a machine → project, holding that WO's bars. */
export interface MachineWorkOrderGroup {
  workOrderKey: string;
  workOrderNumber: string | null;
  workOrderName: string | null;
  /** Bookings on this machine for this WO. */
  bookings: MasterGanttMachineBooking[];
  /** REQ-03: people whose task is booked on this machine for this WO. */
  people: MasterGanttEmployeeAssignment[];
}

/** One project bucket within a machine, holding its work-order groups. */
export interface MachineProjectGroup {
  projectKey: string;
  projectId: string;
  projectName: string;
  workOrders: Map<string, MachineWorkOrderGroup>;
}

/** Top-level machine bucket: per-project → per-work-order nesting. */
export interface MachineGroup {
  machineKey: string;
  machineCode: string;
  machineName: string;
  projects: Map<string, MachineProjectGroup>;
}

/** A person bucket for the catch-all "unassigned to machine" band. */
export interface PersonGroup {
  userKey: string;
  displayName: string;
  assignments: MasterGanttEmployeeAssignment[];
}

/** Context captured per booking/assignment so it can be placed in the tree. */
interface RowContext {
  projectKey: string;
  projectId: string;
  projectName: string;
  workOrderKey: string;
  workOrderNumber: string | null;
  workOrderName: string | null;
}

// ---------------------------------------------------------------------------
// Row-id scheme
// ---------------------------------------------------------------------------

/** Top-level catch-all band for assignments that can't be linked to a machine. */
export const PEOPLE_ROOT_ID = 'people-root';

/** Machine envelope row id. */
export function machineRowId(machineKey: string): string {
  return `mach-${machineKey}`;
}

/** Per-machine Project envelope row id. */
export function machineProjectRowId(machineKey: string, projectKey: string): string {
  return `machproj-${machineKey}-${projectKey}`;
}

/** Per-machine Work Order envelope row id. */
export function machineWorkOrderRowId(machineKey: string, workOrderKey: string): string {
  return `machwo-${machineKey}-${workOrderKey}`;
}

/** Per-work-order People sub-group row id (under a machine's WO row). */
export function machineWorkOrderPeopleRowId(machineKey: string, workOrderKey: string): string {
  return `machwopeople-${machineKey}-${workOrderKey}`;
}

/**
 * BUG-2026-0194 — per-person summary row under a machine's WO People band
 * ("one bar per person, drill down into their tasks, not split by weeks").
 */
export function machineWorkOrderPersonRowId(machineKey: string, workOrderKey: string, userKey: string): string {
  return `machwoperson-${machineKey}-${workOrderKey}-${userKey}`;
}

/** Per-person envelope row id within the catch-all band. */
export function personRowId(userKey: string): string {
  return `people-${userKey}`;
}

// ---------------------------------------------------------------------------
// Pivot
// ---------------------------------------------------------------------------

/**
 * Get (creating if needed) the WorkOrder bucket for a given
 * (machine, project, work order). Defensive: if any ancestor node doesn't yet
 * exist it is created. Used by both the booking pass (machine known from the
 * booking) and the assignment pass (machine = the task's primary machine, which
 * normally already has a booking row for the same task/WO, but we create the
 * node defensively in case it doesn't).
 */
function ensureWorkOrderGroup(
  machineMap: Map<string, MachineGroup>,
  machineKey: string,
  machineCode: string,
  machineName: string,
  ctx: RowContext,
): MachineWorkOrderGroup {
  let machine = machineMap.get(machineKey);
  if (!machine) {
    machine = { machineKey, machineCode, machineName, projects: new Map() };
    machineMap.set(machineKey, machine);
  }

  let project = machine.projects.get(ctx.projectKey);
  if (!project) {
    project = {
      projectKey: ctx.projectKey,
      projectId: ctx.projectId,
      projectName: ctx.projectName,
      workOrders: new Map(),
    };
    machine.projects.set(ctx.projectKey, project);
  }

  let wo = project.workOrders.get(ctx.workOrderKey);
  if (!wo) {
    wo = {
      workOrderKey: ctx.workOrderKey,
      workOrderNumber: ctx.workOrderNumber,
      workOrderName: ctx.workOrderName,
      bookings: [],
      people: [],
    };
    project.workOrders.set(ctx.workOrderKey, wo);
  }

  return wo;
}

/**
 * Pivot the MasterGanttResponse into the machine-first grouping.
 *
 * Pass 1: bookings → (machine → project → work order) buckets, plus a
 * scheduleTaskKey → machineKeys index for linking assignments.
 * Pass 2: assignments → their PRIMARY machine (lowest machineCode) under the
 * assignment's OWN project + work order; unlinkable ones fall to the catch-all.
 */
export function collectGroups(response: MasterGanttResponse): {
  machines: MachineGroup[];
  unlinked: PersonGroup[];
} {
  const machineMap = new Map<string, MachineGroup>();
  const taskToMachineKeys = new Map<string, Set<string>>();
  // Cache machineCode/Name per machineKey so the assignment pass can create
  // ancestor nodes (ensureWorkOrderGroup) with real labels.
  const machineMeta = new Map<string, { code: string; name: string }>();

  // Pass 1: bookings.
  for (const proj of response.projects) {
    for (const wo of proj.workOrders) {
      const ctx = toRowContext(proj.projectKey, proj.projectId, proj.projectName, wo.workOrderKey, wo.workOrderNumber, wo.workOrderName);

      for (const bk of wo.machineBookings) {
        machineMeta.set(bk.machineKey, { code: bk.machineCode, name: bk.machineName });
        const group = ensureWorkOrderGroup(machineMap, bk.machineKey, bk.machineCode, bk.machineName, ctx);
        group.bookings.push(bk);

        if (bk.scheduleTaskKey) {
          let set = taskToMachineKeys.get(bk.scheduleTaskKey);
          if (!set) {
            set = new Set<string>();
            taskToMachineKeys.set(bk.scheduleTaskKey, set);
          }
          set.add(bk.machineKey);
        }
      }
    }
  }

  // Pass 2: assignments.
  const unlinkedMap = new Map<string, PersonGroup>();

  for (const proj of response.projects) {
    for (const wo of proj.workOrders) {
      const ctx = toRowContext(proj.projectKey, proj.projectId, proj.projectName, wo.workOrderKey, wo.workOrderNumber, wo.workOrderName);

      for (const ea of wo.employeeAssignments) {
        const machineKeys = ea.scheduleTaskKey ? taskToMachineKeys.get(ea.scheduleTaskKey) : undefined;
        const primaryMachineKey =
          machineKeys && machineKeys.size > 0 ? pickPrimaryMachineKey(machineKeys, machineMeta) : null;

        if (primaryMachineKey) {
          const meta = machineMeta.get(primaryMachineKey);
          // Defensive: the primary machine normally already has this WO node
          // (it carries the booking for the same task), but create it if not.
          const group = ensureWorkOrderGroup(
            machineMap,
            primaryMachineKey,
            meta?.code ?? primaryMachineKey,
            meta?.name ?? '',
            ctx,
          );
          group.people.push(ea);
        } else {
          let group = unlinkedMap.get(ea.userKey);
          if (!group) {
            group = { userKey: ea.userKey, displayName: ea.displayName, assignments: [] };
            unlinkedMap.set(ea.userKey, group);
          }
          group.assignments.push(ea);
        }
      }
    }
  }

  // Stable, human-friendly ordering: machines by code, projects by name, work
  // orders by number (then name), people by name.
  const machines = Array.from(machineMap.values()).sort((a, b) =>
    a.machineCode.localeCompare(b.machineCode),
  );
  for (const machine of machines) {
    for (const project of machine.projects.values()) {
      for (const wo of project.workOrders.values()) {
        wo.people.sort((a, b) => a.displayName.localeCompare(b.displayName));
      }
    }
  }
  const unlinked = Array.from(unlinkedMap.values()).sort((a, b) =>
    a.displayName.localeCompare(b.displayName),
  );

  return { machines, unlinked };
}

function toRowContext(
  projectKey: string,
  projectId: string,
  projectName: string,
  workOrderKey: string,
  workOrderNumber: string | null | undefined,
  workOrderName: string | null | undefined,
): RowContext {
  return {
    projectKey,
    projectId,
    projectName,
    workOrderKey,
    workOrderNumber: workOrderNumber ?? null,
    workOrderName: workOrderName ?? null,
  };
}

/**
 * Pick the primary machine for an assignment whose task spans multiple machines:
 * the machine with the lowest machineCode (deterministic, human-friendly), so
 * the same assignment always lands on the same single machine row.
 */
function pickPrimaryMachineKey(
  machineKeys: Set<string>,
  machineMeta: Map<string, { code: string; name: string }>,
): string {
  let best: string | null = null;
  let bestCode = '';
  for (const key of machineKeys) {
    const code = machineMeta.get(key)?.code ?? key;
    if (best === null || code.localeCompare(bestCode) < 0) {
      best = key;
      bestCode = code;
    }
  }
  return best!;
}

// ---------------------------------------------------------------------------
// Sort helpers
// ---------------------------------------------------------------------------

/** Sorted projects within a machine, ordered by project name. */
export function sortedProjects(machine: MachineGroup): MachineProjectGroup[] {
  return Array.from(machine.projects.values()).sort((a, b) =>
    a.projectName.localeCompare(b.projectName),
  );
}

/**
 * Sorted work orders within a machine's project, ordered by work-order number
 * then name (nulls sort first via empty-string fallback).
 */
export function sortedWorkOrders(project: MachineProjectGroup): MachineWorkOrderGroup[] {
  return Array.from(project.workOrders.values()).sort((a, b) => {
    const numCmp = (a.workOrderNumber ?? '').localeCompare(b.workOrderNumber ?? '');
    if (numCmp !== 0) return numCmp;
    return (a.workOrderName ?? '').localeCompare(b.workOrderName ?? '');
  });
}

// ---------------------------------------------------------------------------
// Date-span helpers for envelope rows
// ---------------------------------------------------------------------------

/** Collect inclusive start dates from a WO group's bookings + people. */
export function workOrderStarts(wo: MachineWorkOrderGroup): (string | null | undefined)[] {
  const starts: (string | null | undefined)[] = wo.bookings.map(b => b.startDate);
  for (const ea of wo.people) starts.push(ea.startDate ?? ea.weekStart);
  return starts;
}

/** Collect inclusive end dates from a WO group's bookings + people. */
export function workOrderEnds(wo: MachineWorkOrderGroup): (string | null | undefined)[] {
  const ends: (string | null | undefined)[] = wo.bookings.map(b => b.endDate);
  for (const ea of wo.people) ends.push(ea.endDate ?? addDays(ea.weekStart, 6));
  return ends;
}
